import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const CartContext = createContext();

export function CartProvider({ children }) {
  const { user, isWholesale } = useAuth();

  // Generate user-specific cart key
  const getCartKey = useCallback(() => {
    return user ? `cart_user_${user.id}` : 'cart_guest';
  }, [user]);

  const [items, setItems] = useState(() => {
    const cartKey = user ? `cart_user_${user.id}` : 'cart_guest';
    const saved = localStorage.getItem(cartKey);
    return saved ? JSON.parse(saved) : [];
  });

  // Clear cart when user changes (login/logout)
  useEffect(() => {
    const cartKey = getCartKey();
    const saved = localStorage.getItem(cartKey);
    setItems(saved ? JSON.parse(saved) : []);
  }, [getCartKey]);

  // Save cart to user-specific key
  useEffect(() => {
    const cartKey = getCartKey();
    localStorage.setItem(cartKey, JSON.stringify(items));
  }, [items, getCartKey]);

  const addItem = (product, quantity = 1) => {
    setItems(prev => {
      const existing = prev.find(item => item.product.id === product.id);
      if (existing) {
        return prev.map(item =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, { product, quantity: quantity || getMinQty(product) }];
    });
  };

  const removeItem = (productId) => {
    setItems(prev => prev.filter(item => item.product.id !== productId));
  };

  const clearCart = () => {
    setItems([]);
  };

  const getItemPrice = (product) => {
    if (!product) return 0;
    if (user?.role === 'pos_user') {
      return product.pos_price || product.selling_price;
    }
    if (isWholesale) {
      return product.wholesale_price || product.selling_price;
    }
    return product.selling_price;
  };

  const getMinQty = (product) => {
    if (!product) return 1;
    // POS users might not have a minimum, or it defaults to 1
    if (user?.role === 'pos_user') return 1;

    if (isWholesale) {
      return product.wholesale_min_qty || 10;
    }

    return product.minimum_purchase_qty || 1;
  };

  const updateQuantity = (productId, quantity) => {
    setItems(prev => {
      const item = prev.find(i => i.product.id === productId);
      if (!item) return prev;

      const minQty = getMinQty(item.product);

      // If trying to set below min qty, but not 0 (deletion), enforce min
      if (quantity < minQty && quantity > 0) {
        return prev.map(i =>
          i.product.id === productId ? { ...i, quantity: minQty } : i
        );
      }

      if (quantity <= 0) {
        return prev.filter(i => i.product.id !== productId);
      }

      return prev.map(i =>
        i.product.id === productId ? { ...i, quantity } : i
      );
    });
  };

  const getSubtotal = () => {
    return items.reduce((total, item) => {
      return total + (getItemPrice(item.product) * item.quantity);
    }, 0);
  };

  const getItemCount = () => {
    return items.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{
      items,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      getSubtotal,
      getItemCount,
      getItemPrice,
      getMinQty
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
